import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  CheckCircle2, 
  Clock, 
  XCircle, 
  AlertTriangle, 
  AlertCircle, 
  Upload,
  FileText 
} from 'lucide-react';
import { useRequiredDocuments, type DocumentStatus } from '@/hooks/useRequiredDocuments';
import { format } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import { useState } from 'react';
import { PortalDocumentUpload } from './PortalDocumentUpload';
import { useQueryClient } from '@tanstack/react-query';

interface RequiredDocumentsChecklistProps {
  companyId: string;
  onDocumentClick?: (documentTypeName: string) => void;
}

export function RequiredDocumentsChecklist({ companyId, onDocumentClick }: RequiredDocumentsChecklistProps) {
  const { requiredDocuments, stats, isLoading } = useRequiredDocuments(companyId);

  const getStatusIcon = (status: DocumentStatus) => {
    switch (status) {
      case 'verified':
        return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-600" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'expired':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      case 'expiring_soon':
        return <AlertTriangle className="h-5 w-5 text-orange-600" />;
      case 'missing':
        return <FileText className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status: DocumentStatus) => {
    switch (status) {
      case 'verified':
        return <Badge variant="default" className="bg-green-600">Verified</Badge>;
      case 'pending':
        return <Badge variant="secondary" className="bg-yellow-600">Pending Review</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      case 'expired':
        return <Badge variant="destructive">Expired</Badge>;
      case 'expiring_soon':
        return <Badge variant="secondary" className="bg-orange-600">Expiring Soon</Badge>;
      case 'missing':
        return <Badge variant="outline">Not Uploaded</Badge>;
    }
  };

  const getStatusColor = (status: DocumentStatus) => {
    switch (status) {
      case 'verified':
        return 'border-green-200 bg-green-50 dark:bg-green-950/20';
      case 'pending':
        return 'border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20';
      case 'rejected':
      case 'expired':
        return 'border-red-200 bg-red-50 dark:bg-red-950/20';
      case 'expiring_soon':
        return 'border-orange-200 bg-orange-50 dark:bg-orange-950/20';
      default:
        return 'border-muted';
    }
  };

  const needsAction = (status: DocumentStatus) => {
    return ['missing', 'rejected', 'expired', 'expiring_soon'].includes(status);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Prequalification Requirements</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (requiredDocuments.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Prequalification Requirements</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No documents are currently required for prequalification.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Prequalification Requirements</span>
        </CardTitle>
          <div className="pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                Complete these documents to become prequalified
              </span>
              <span className="font-medium">
                {stats.verified} of {stats.total} verified
              </span>
            </div>
            <Progress value={stats.completionPercentage} className="h-2" />
            <div className="flex gap-2 text-xs text-muted-foreground">
              {stats.missing > 0 && <span>{stats.missing} missing</span>}
              {stats.pending > 0 && <span>{stats.pending} pending</span>}
              {stats.rejected > 0 && <span>{stats.rejected} rejected</span>}
              {stats.expiring > 0 && <span>{stats.expiring} expiring</span>}
              {stats.expired > 0 && <span>{stats.expired} expired</span>}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {stats.isPrequalified && (
            <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 rounded-lg p-4 mb-4">
              <p className="text-sm text-green-800 dark:text-green-200 font-medium">
                🎉 All required documents verified! You're prequalified.
              </p>
            </div>
          )}

          {requiredDocuments.map((item) => (
            <div
              key={item.documentType.name}
              onClick={() => needsAction(item.status) && onDocumentClick?.(item.documentType.name)}
              className={`border rounded-lg p-4 transition-all ${getStatusColor(item.status)} ${
                needsAction(item.status) ? 'cursor-pointer hover:shadow-md hover:scale-[1.01]' : ''
              }`}
              role={needsAction(item.status) ? 'button' : undefined}
              tabIndex={needsAction(item.status) ? 0 : undefined}
              onKeyDown={(e) => {
                if (needsAction(item.status) && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onDocumentClick?.(item.documentType.name);
                }
              }}
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  {getStatusIcon(item.status)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1">
                      <h4 className="font-medium text-sm mb-1">
                        {item.documentType.name}
                        {item.documentType.required_for_prequalification && (
                          <Badge variant="destructive" className="ml-2 text-xs">Required</Badge>
                        )}
                      </h4>
                    </div>
                    {getStatusBadge(item.status)}
                  </div>

                  {/* Document details */}
                  {item.uploadedDocument && (
                    <div className="text-xs space-y-1 mb-2">
                      {item.uploadedDocument.document_number && (
                        <p className="text-muted-foreground">
                          <span className="font-medium">Number:</span> {item.uploadedDocument.document_number}
                        </p>
                      )}
                      {item.uploadedDocument.expiry_date && (
                        <p className="text-muted-foreground">
                          <span className="font-medium">Expires:</span>{' '}
                          {format(new Date(item.uploadedDocument.expiry_date), 'MMM d, yyyy')}
                          {item.daysUntilExpiry !== undefined && item.daysUntilExpiry > 0 && (
                            <span className="ml-1">
                              ({item.daysUntilExpiry} day{item.daysUntilExpiry === 1 ? '' : 's'} remaining)
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Warnings and messages */}
                  {item.status === 'missing' && (
                    <p className="text-xs text-muted-foreground mb-2">
                      📋 This document has not been uploaded yet
                    </p>
                  )}
                  {item.status === 'pending' && (
                    <p className="text-xs text-muted-foreground mb-2">
                      ⏳ Your document is awaiting review by the project team
                    </p>
                  )}
                  {item.rejectionReason && (
                    <p className="text-xs text-red-600 dark:text-red-400 mb-2">
                      ❌ {item.rejectionReason}
                    </p>
                  )}
                  {item.expiryWarning && (
                    <p className="text-xs text-orange-600 dark:text-orange-400 mb-2">
                      ⚠️ {item.expiryWarning}
                    </p>
                  )}

                  {/* Click hint for action items */}
                  {needsAction(item.status) && (
                    <p className="text-xs text-muted-foreground italic">
                      💡 Click this card to upload {item.status === 'missing' ? '' : 'an updated'} document
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
  );
}
